import { OrgPublicDetail } from "@garther/interfaces";
import { useReviews } from "@garther/query-hooks";
import { Avatar, Box, Image, SelectableCard, TiptapView } from "@garther/ui/components";
import { Button, Separator } from "@garther/ui/components";
import { toast, useI18n } from "@garther/ui/libs";
import {
  Award,
  Calendar,
  Globe,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Share2,
  Star,
  TrendingUp,
  Users
} from "lucide-react";
import { useState } from "react";

import { Events } from "@/features/event/components/events";
import { RawLayout } from "@/shared/components/layout";

import { LeaveReviewModal } from "./review-modal";

interface OrgPageProps {
  org: OrgPublicDetail;
}

export function OrgPage({ org }: OrgPageProps) {
  const { formatDate, t } = useI18n();
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const { data: reviews, refetch: refetchReviews } = useReviews({
    orgId: org.id
  });

  return (
    <RawLayout>
      <div className="min-h-screen bg-background">
        {/* Hero Banner */}
        <div className="relative h-64 sm:h-80 lg:h-96 bg-background-secondary">
          <Image src={org.cover} alt={org.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-background-primary via-background-primary/80 via-20% to-transparent to-60%" />
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          {/* Company Header */}
          <div className="relative -mt-20 sm:-mt-20 mb-8">
            <div className="flex flex-col sm:flex-row items-start gap-6">
              {/* Logo */}
              <div className="relative">
                <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-xl overflow-hidden bg-card border-[3px] border-border-light shadow-lg">
                  <Image src={org.avatar} alt={org.name} className="w-full h-full object-cover" />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 space-y-3">
                <div className="space-y-1">
                  <div className="flex flex-wrap items-start gap-2">
                    <p className="text-responsive-5xl font-normal text-text-primary">{org.name}</p>
                    {org.verified && (
                      <div className="flex items-center gap-1 text-text-primary">
                        <Award className="h-4 w-4" />
                      </div>
                    )}
                  </div>
                  <p className="text-responsive-base text-text-primary font-normal">{org.tagline}</p>
                </div>

                <div className="flex items-center gap-1">
                  <Star className="h-5 w-5 fill-amber-400 text-amber-400" />
                  <span className="font-medium">{org.stats.rating}</span>
                  <span className="text-muted-foreground">({org.stats.totalReviews} reviews)</span>
                </div>

                <Button
                  variant="outline"
                  padding="px-2 py-1"
                  className="px-2 text-responsive-base"
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success(t("org.actions.linkCopied"));
                  }}
                >
                  <Share2 className="h-4 w-4 mr-1" />
                  {t("org.actions.share")}
                </Button>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* About Section */}
              <Box className="space-y-4">
                <p className="text-responsive-md font-normal text-text-primary">
                  {t("org.about.title", { orgName: org.name })}
                </p>
                <TiptapView content={org.description as string} variant="normal" />
                {org.mission && (
                  <div className="bg-background-highlight rounded-sm p-4">
                    <p className="text-responsive-sm font-medium text-text-secondary pb-2">
                      {t("org.about.mission")}
                    </p>
                    <TiptapView content={org.mission as string} variant="compact" />
                  </div>
                )}
              </Box>

              {/* Featured Events */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-responsive-md font-normal text-text-primary">
                      {t("org.events.featured")}
                    </p>
                    <p className="text-responsive-base font-medium text-text-secondary">
                      {t("org.events.subtitle")}
                    </p>
                  </div>
                </div>
                <Events hostId={org.id} />
              </div>

              {/* Reviews Section */}
              <Box className="space-y-6">
                <div>
                  <p className="text-responsive-md font-normal text-text-primary">{t("org.reviews.title")}</p>
                  <p className="text-responsive-base font-normal text-text-secondary">
                    {t("org.reviews.subtitle")}
                  </p>
                </div>

                {/* Rating Summary */}
                <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 p-4 sm:p-6 bg-background-highlight rounded-sm">
                  <div className="flex flex-col items-center justify-center text-center space-y-2">
                    <div className="text-responsive-4xl font-medium">{org.stats.rating}</div>
                    <div className="flex items-center gap-1">
                      {Array(5)
                        .fill(0)
                        .map((_, value) => (
                          <Star
                            key={value}
                            className={`h-4 w-4 ${
                              value < Math.floor(org.stats.rating)
                                ? "fill-amber-400 text-amber-400"
                                : "text-text-secondary"
                            }`}
                          />
                        ))}
                    </div>
                    <p className="text-text-secondary text-responsive-base">
                      {t("org.reviews.basedOn", {
                        count: org.stats.totalReviews
                      })}
                    </p>
                  </div>

                  <div className="space-y-1">
                    {["5", "4", "3", "2", "1"].map((value) => (
                      <div key={value} className="flex items-center gap-3">
                        <div className="flex items-center gap-1 w-6">
                          <span className="text-responsive-base">{value}</span>
                          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                        </div>
                        <div className="flex-1 h-2">
                          <div className="w-full h-2 bg-background-highlight rounded-sm">
                            <div
                              className={`h-full bg-background-dark-secondary rounded-sm`}
                              style={{
                                width: `${((org.stats.ratingDistribution[value as keyof typeof org.stats.ratingDistribution] ?? 0) / org.stats.totalReviews) * 100}%`
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Individual Reviews */}
                <div className="space-y-4">
                  {reviews?.map((review) => (
                    <div key={review.id} className="p-4 rounded-sm border border-border space-y-3">
                      <div className="flex items-start gap-3">
                        <Avatar
                          className="h-10 w-10"
                          src={review.user.avatar ?? undefined}
                          name={review.user.name}
                        />
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center justify-between">
                            <p className="font-normal text-text-primary text-responsive-base">
                              {review.user.name}
                            </p>
                            <span className="text-text-secondary text-responsive-sm">
                              {formatDate(review.createdAt)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-3 w-3 ${
                                  i < review.rating ? "fill-amber-400 text-amber-400" : "text-text-secondary"
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-text-primary text-responsive-base">{review.comment}</p>
                    </div>
                  ))}
                </div>

                <Button variant="outline" className="w-full" onClick={() => setIsReviewModalOpen(true)}>
                  <MessageCircle className="h-4 w-4 mr-2" />
                  {t("org.reviews.leaveReview")}
                </Button>
              </Box>
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              {/* Stats Card */}
              <Box className="space-y-4">
                <p className="text-responsive-md font-normal text-text-primary">
                  {t("org.performance.title")}
                </p>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 px-4">
                      <Calendar className="h-5 w-5 text-text-secondary" />
                      <div>
                        <p className="text-responsive-sm font-medium text-text-secondary">
                          {t("org.performance.totalEvents")}
                        </p>
                        <p className="font-normal text-responsive-base">{org.stats.totalEvents}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 px-4">
                      <Users className="h-5 w-5 text-text-secondary" />
                      <div>
                        <p className="text-responsive-sm font-medium text-text-secondary">
                          {t("org.performance.avgAttendance")}
                        </p>
                        <p className="font-normal text-responsive-base">{org.stats.avgAttendance}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 px-4">
                      <TrendingUp className="h-5 w-5 text-text-secondary" />
                      <div>
                        <p className="text-responsive-sm font-medium text-text-secondary">
                          {t("org.performance.thisMonth")}
                        </p>
                        <p className="font-normal text-responsive-base">
                          {t("org.performance.eventsThisMonth", {
                            count: org.stats.eventsThisMonth
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </Box>

              {/* Contact Card */}
              <Box className="space-y-4">
                <p className="text-responsive-md font-normal text-text-primary">{t("org.contact.title")}</p>
                <div className="space-y-3">
                  {org.contact.email && (
                    <SelectableCard
                      className="w-full flex items-center gap-3 px-4 py-2"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(`mailto:${org.contact.email}`, "_blank");
                      }}
                    >
                      <Mail className="h-5 w-5 text-text-secondary" />
                      <div className="flex-1 min-w-0">
                        <p className="text-responsive-sm font-medium text-text-secondary">
                          {t("org.contact.email")}
                        </p>
                        <p className="text-responsive-base font-normal text-text-primary truncate">
                          {org.contact.email}
                        </p>
                      </div>
                    </SelectableCard>
                  )}
                  {org.contact.phone && (
                    <SelectableCard
                      className="w-full flex items-center gap-3 px-4 py-2"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(`tel:${org.contact.phone}`, "_blank");
                      }}
                    >
                      <Phone className="h-5 w-5 text-text-secondary" />
                      <div className="flex-1 min-w-0">
                        <p className="text-responsive-sm font-medium text-text-secondary">
                          {t("org.contact.phone")}
                        </p>
                        <p className="text-responsive-base font-normal text-text-primary truncate">
                          {org.contact.phone}
                        </p>
                      </div>
                    </SelectableCard>
                  )}
                  {org.contact.website && (
                    <SelectableCard
                      className="w-full flex items-center gap-3 px-4 py-2"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(`https://${org.contact.website}`, "_blank");
                      }}
                    >
                      <Globe className="h-5 w-5 text-text-secondary" />
                      <div className="flex-1 min-w-0">
                        <p className="text-responsive-sm font-medium text-text-secondary">
                          {t("org.contact.website")}
                        </p>
                        <p className="text-responsive-base font-normal text-text-primary truncate">
                          {org.contact.website}
                        </p>
                      </div>
                    </SelectableCard>
                  )}
                </div>

                <Separator />

                <div className="space-y-2">
                  <p className="text-responsive-sm font-normal text-text-secondary">
                    {t("org.contact.socialMedia")}
                  </p>
                  <div className="flex gap-2">
                    {org.contact.instagram && (
                      <Button
                        variant="outline"
                        padding="px-2 py-2"
                        className="flex-1 text-responsive-base"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(org.contact.instagram ?? "", "_blank");
                        }}
                      >
                        Instagram
                      </Button>
                    )}
                    {org.contact.facebook && (
                      <Button
                        variant="outline"
                        padding="px-2 py-2"
                        className="flex-1 text-responsive-base"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(org.contact.facebook ?? "", "_blank");
                        }}
                      >
                        Facebook
                      </Button>
                    )}
                  </div>
                </div>
              </Box>

              {/* Location Card */}
              <Box className="space-y-4">
                <p className="text-responsive-md font-normal text-text-primary">{t("org.location.title")}</p>
                <div className="space-y-2">
                  {org.locations.map((location) => (
                    <SelectableCard
                      key={location.id}
                      className="flex items-center gap-3 hover:bg-background-highlight rounded-sm px-4 py-2"
                      onClick={() => {
                        window.open(
                          `https://www.google.com/maps?q=${location.lat},${location.long}`,
                          "_blank"
                        );
                      }}
                    >
                      <MapPin className="h-5 w-5 text-text-secondary" />

                      <div className="flex-1">
                        <p className="text-responsive-sm font-medium text-text-secondary">
                          {t("org.location.address")}
                        </p>
                        <p className="text-responsive-base font-normal text-text-primary">{location.name}</p>
                      </div>
                    </SelectableCard>
                  ))}
                </div>
              </Box>
            </div>
          </div>
        </div>
      </div>
      <LeaveReviewModal
        orgId={org.id}
        orgName={org.name}
        open={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        onReviewSubmitted={() => refetchReviews()}
      />
    </RawLayout>
  );
}
